/**
 * Exchange API 由来 action の保存と fiscal lock 退避を担当する。
 * single action rule は今回挿入した action にだけ適用し、確定済み action を source 共有で巻き込まない。
 */
import type { Action } from '@0xtorch/core'
import { divideArrayIntoChunks } from '@pkg/basic'
import { and, eq, inArray, ne } from 'drizzle-orm'
import {
  actionSourceColumnCount,
  singleActionRuleColumnCount,
  sqliteMaxHostParameterCount,
} from '../../constants'
import {
  actionSourceTable,
  actionTable,
  lockedPeriodActionTable,
  portfolioTable,
  singleActionRuleTable,
} from '../../schema'
import type { DatabaseWithTransaction, TransactionDatabase } from '../../types'
import { getMaxInsertRowCount } from '../../utils'
import { applyActionEditRulesToInsertedActionsOnDatabase } from './applyActionEditRulesToInsertedActions'
import { deleteAssistantProposalsForActionKeys } from './deleteAssistantProposalsForActionKeys'
import {
  applyRulesToLockedInsertRowsInTransaction,
  createActionAccountRelationRowsForAccount,
  createAssetInsertQueries,
  createLockedActionInsertQueries,
  createOpenActionInsertQueries,
  prepareFiscalLockInsertRows,
} from './fiscalLock'

type SaveExchangeActionsParameters = {
  database: DatabaseWithTransaction
  accountId: number
  actions: readonly Action[]
  transferSources: readonly string[]
}

type ExchangeActionRule = typeof singleActionRuleTable.$inferInsert

/**
 * Exchange再同期で実際に置換する未lock Actionの関連proposalだけを削除する。
 * 同じsourceに残るlock済みActionをreferenceにするproposalは削除対象に含めない。
 * @param params transaction内DBと再同期source一覧。
 * @returns 削除完了後にresolveする。
 */
const deleteAssistantProposalsForOpenSourceActions = async ({
  database,
  sourceIds,
}: {
  readonly database: TransactionDatabase
  readonly sourceIds: readonly string[]
}): Promise<void> => {
  const actionKeys: {
    sourceId: string
    actionOrder: number
  }[] = []
  for (const chunk of divideArrayIntoChunks(
    [...new Set(sourceIds)],
    sqliteMaxHostParameterCount,
  )) {
    const rows = await database
      .select({
        sourceId: actionTable.sourceId,
        actionOrder: actionTable.order,
      })
      .from(actionTable)
      .where(
        and(
          inArray(actionTable.sourceId, [...chunk]),
          ne(actionTable.locked, true),
        ),
      )
    actionKeys.push(...rows)
  }
  await deleteAssistantProposalsForActionKeys({ database, actionKeys })
}

/**
 * Exchange の single action rule 更新 query を今回挿入した action のみに絞って構築する。
 * @param params transaction、rule 一覧、更新対象 action id 一覧を受け取る。
 * @returns 対象 action のみに rule を適用する update query 一覧を返す。
 */
const createExchangeRuleUpdateQueries = (params: {
  readonly tx: TransactionDatabase
  readonly rules: readonly ExchangeActionRule[]
  readonly actionIds: readonly string[]
}): Promise<unknown>[] => {
  const { tx, rules, actionIds } = params
  if (rules.length === 0 || actionIds.length === 0) {
    return []
  }

  return rules.flatMap((rule) =>
    divideArrayIntoChunks(actionIds, sqliteMaxHostParameterCount).map((chunk) =>
      tx
        .update(actionTable)
        .set({
          type: rule.newType,
          evidence: 'user-rule',
          generatedTx: false,
        })
        .where(
          and(
            inArray(actionTable.id, [...chunk]),
            eq(actionTable.sourceId, rule.source),
            eq(actionTable.type, rule.targetType),
            ne(actionTable.locked, true),
          ),
        ),
    ),
  )
}

/** exchange API から取得した actions , sources を保存
 * - portfolio の　isTxGenerated を false に変更
 * - action source に紐づく action account relation 削除
 * - action source に紐づく action transfer 削除
 * - action source に紐づく action 削除
 * - action 関連データを保存
 *   - action source (on conflict do nothing)
 *   - action
 *   - action transfer
 *   - crypto currency
 *   - nft
 *   - action account relation
 * - transfer source から作成した single action rule を保存 (on conflict do nothing)
 * - single action rule に該当する action を更新
 */
export const saveExchangeActions = async ({
  database: { database, transaction },
  accountId,
  actions,
  transferSources,
}: SaveExchangeActionsParameters) => {
  if (actions.length === 0 && transferSources.length === 0) {
    return
  }

  const {
    openActions,
    openActionIds: insertedActionIdList,
    lockedActions,
    openInsertRows,
    lockedInsertRows,
  } = await prepareFiscalLockInsertRows({
    database: { database, transaction },
    actions,
    createLockedActionAccountRelations: ({ lockedActions }) =>
      createActionAccountRelationRowsForAccount({
        actions: lockedActions,
        accountId,
      }),
  })

  const actionSourceSet = new Set<string>()
  for (const action of actions) {
    actionSourceSet.add(action.source)
  }
  const actionSources = [...actionSourceSet]
  const openSources = [...new Set(openActions.map((action) => action.source))]
  const lockedSources = [
    ...new Set(lockedActions.map((action) => action.source)),
  ]

  const actionAccountRelations = createActionAccountRelationRowsForAccount({
    actions: openActions,
    accountId,
  })
  const shouldInvalidateCalculation = insertedActionIdList.length > 0

  const rules: ExchangeActionRule[] = []
  for (const source of transferSources) {
    // `gift` to `receive-from-cex`
    rules.push({
      source,
      targetType: 'gift',
      newType: 'receive-from-cex',
    })
    // `income` to `receive-from-cex`
    rules.push({
      source,
      targetType: 'income',
      newType: 'receive-from-cex',
    })
    // `fee` to `send-to-cex`
    rules.push({
      source,
      targetType: 'fee',
      newType: 'send-to-cex',
    })
    // `transfer` to `send-to-cex`
    rules.push({
      source,
      targetType: 'transfer',
      newType: 'send-to-cex',
    })
  }

  await transaction([
    (tx) => [
      deleteAssistantProposalsForOpenSourceActions({
        database: tx,
        sourceIds: openSources,
      }),
    ],
    (tx) => [
      ...(shouldInvalidateCalculation
        ? [tx.update(portfolioTable).set({ isTxGenerated: false })]
        : []),
      // open action source に紐づく action 削除 (確定済 action は保持する)
      ...divideArrayIntoChunks(openSources, sqliteMaxHostParameterCount).map(
        (chunk) =>
          tx
            .delete(actionTable)
            .where(
              and(
                inArray(actionTable.sourceId, [...chunk]),
                ne(actionTable.locked, true),
              ),
            ),
      ),
      ...divideArrayIntoChunks(lockedSources, sqliteMaxHostParameterCount).map(
        (chunk) =>
          tx
            .delete(lockedPeriodActionTable)
            .where(inArray(lockedPeriodActionTable.sourceId, [...chunk])),
      ),
    ],
    (tx) => [
      // action source を保存 (on conflict do nothing)
      ...divideArrayIntoChunks(
        actionSources,
        getMaxInsertRowCount(actionSourceColumnCount),
      ).map((chunk) =>
        tx
          .insert(actionSourceTable)
          .values(chunk.map((id) => ({ id, type: 'other' as const })))
          .onConflictDoNothing(),
      ),
      ...createAssetInsertQueries({ tx, openInsertRows, lockedInsertRows }),
    ],
    (tx) => [
      (async () => {
        const preparedLockedInsertRows =
          await applyRulesToLockedInsertRowsInTransaction({
            database: tx,
            lockedInsertRows,
            beforeApplyActionEditRules: async ({
              database: transactionDatabase,
              actionIds,
            }) => {
              await Promise.all(
                createExchangeRuleUpdateQueries({
                  tx: transactionDatabase,
                  rules,
                  actionIds,
                }),
              )
            },
          })

        await Promise.all([
          ...createOpenActionInsertQueries({
            tx,
            openInsertRows,
            actionAccountRelationRows: actionAccountRelations,
          }),
          ...divideArrayIntoChunks(
            rules,
            getMaxInsertRowCount(singleActionRuleColumnCount),
          ).map((chunk) =>
            tx
              .insert(singleActionRuleTable)
              .values([...chunk])
              .onConflictDoNothing(),
          ),
          ...createExchangeRuleUpdateQueries({
            tx,
            rules,
            actionIds: insertedActionIdList,
          }),
          ...createLockedActionInsertQueries({
            tx,
            lockedInsertRows: preparedLockedInsertRows,
          }),
        ])

        if (insertedActionIdList.length > 0) {
          await applyActionEditRulesToInsertedActionsOnDatabase({
            database: tx,
            actionIdList: insertedActionIdList,
          })
        }
      })(),
    ],
  ])
}
